import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FlashOnIcon from '@mui/icons-material/FlashOn';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`qa-tabpanel-${index}`}
      aria-labelledby={`qa-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

/**
 * ⚡ QUICK ACTIONS PAGE
 * Administrative quick actions for the Admin Dashboard
 * - Tab 1: Diagnostic (lot status check)
 * - Tab 2: Send lots back to validation
 * - Tab 3: Delete lots (DANGEROUS)
 */
export default function QuickActionsPage() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Common state
  const [lotInput, setLotInput] = useState('');

  // Diagnostic state
  const [diagnosticResults, setDiagnosticResults] = useState<any>(null);

  // Send to validation state
  const [sendValidationPreview, setSendValidationPreview] = useState<any>(null);
  const [sendValidationConfirmed, setSendValidationConfirmed] = useState(false);
  const [sendValidationResult, setSendValidationResult] = useState<any>(null);

  // Delete lots state
  const [deletePreview, setDeletePreview] = useState<any>(null);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteResult, setDeleteResult] = useState<any>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Reset state when changing tabs
    setError(null);
    setSuccess(null);
    setLotInput('');
    setDiagnosticResults(null);
    setSendValidationPreview(null);
    setSendValidationConfirmed(false);
    setSendValidationResult(null);
    setDeletePreview(null);
    setDeleteConfirmed(false);
    setDeleteConfirmText('');
    setDeleteResult(null);
  };

  // Parse lot numbers from input
  const parseLotNumbers = (input: string): string[] => {
    return input
      .split(/[,\n]+/)
      .map(lot => lot.trim())
      .filter(lot => lot.length > 0);
  };

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem('access_token');
  };

  // API call helper
  const apiCall = async (endpoint: string, body: any) => {
    const token = getToken();
    const response = await fetch(`/api/v1/quick-actions/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Operation failed');
    }

    return response.json();
  };

  // ==================== DIAGNOSTIC TAB ====================

  const handleDiagnosticLoad = async () => {
    setLoading(true);
    setError(null);
    setDiagnosticResults(null);

    try {
      const lotNumbers = parseLotNumbers(lotInput);
      if (lotNumbers.length === 0) {
        throw new Error('Please enter at least one lot number');
      }

      const result = await apiCall('lot-status', { lotNumbers });
      setDiagnosticResults(result);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ==================== SEND TO VALIDATION TAB ====================

  const handleSendValidationPreview = async () => {
    setLoading(true);
    setError(null);
    setSendValidationPreview(null);
    setSendValidationConfirmed(false);
    setSendValidationResult(null);

    try {
      const lotNumbers = parseLotNumbers(lotInput);
      if (lotNumbers.length === 0) {
        throw new Error('Please enter at least one lot number');
      }

      const result = await apiCall('preview-send-to-validation', { lotNumbers });
      setSendValidationPreview(result);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendValidationExecute = async () => {
    if (!sendValidationConfirmed) {
      setError('Please confirm the action');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const lotNumbers = parseLotNumbers(lotInput);
      const result = await apiCall('send-to-validation', { lotNumbers, confirmed: true });
      setSendValidationResult(result);
      setSuccess(`✅ Successfully sent ${result.lotsProcessed} lots back to validation`);
      // Clear preview and confirmation
      setSendValidationPreview(null);
      setSendValidationConfirmed(false);
      setLotInput('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ==================== DELETE LOTS TAB ====================

  const handleDeletePreview = async () => {
    setLoading(true);
    setError(null);
    setDeletePreview(null);
    setDeleteConfirmed(false);
    setDeleteConfirmText('');
    setDeleteResult(null);

    try {
      const lotNumbers = parseLotNumbers(lotInput);
      if (lotNumbers.length === 0) {
        throw new Error('Please enter at least one lot number');
      }

      const result = await apiCall('preview-delete-lots', { lotNumbers });
      setDeletePreview(result);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExecute = async () => {
    if (!deleteConfirmed) {
      setError('Please confirm the action');
      return;
    }

    if (deleteConfirmText !== 'DELETE') {
      setError('Please type "DELETE" to confirm');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const lotNumbers = parseLotNumbers(lotInput);
      const result = await apiCall('delete-lots', { 
        lotNumbers, 
        confirmed: true, 
        confirmationText: deleteConfirmText 
      });
      setDeleteResult(result);
      setSuccess(`⚠️ Successfully deleted ${result.lotsDeleted} lots (${result.samplesDeleted} samples)`);
      // Clear preview and confirmation
      setDeletePreview(null);
      setDeleteConfirmed(false);
      setDeleteConfirmText('');
      setLotInput('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Paper elevation={3} sx={{ width: '100%', mb: 2 }}>
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <FlashOnIcon sx={{ fontSize: 40, color: 'warning.main' }} />
          <Box>
            <Typography variant="h4" gutterBottom>
              ⚡ Actions Rapides
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Opérations administratives rapides (Admin uniquement)
            </Typography>
          </Box>
        </Box>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="quick actions tabs"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}
        >
          <Tab 
            icon={<SearchIcon />} 
            label="Diagnostic" 
            iconPosition="start"
            id="qa-tab-0"
            aria-controls="qa-tabpanel-0"
          />
          <Tab 
            icon={<RestoreIcon />} 
            label="Renvoyer à Validation" 
            iconPosition="start"
            id="qa-tab-1"
            aria-controls="qa-tabpanel-1"
          />
          <Tab 
            icon={<DeleteIcon />} 
            label="Supprimer Lots" 
            iconPosition="start"
            id="qa-tab-2"
            aria-controls="qa-tabpanel-2"
            sx={{ color: 'error.main' }}
          />
        </Tabs>

        {/* ==================== TAB 1: DIAGNOSTIC ==================== */}
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            🔍 Diagnostic de Lots (Lecture seule)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Vérifiez rapidement le statut, les certificats et les flags de validation pour un ou plusieurs lots.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Numéros de Lots"
                placeholder="R-9077, R-9763, R-9430&#10;ou un lot par ligne"
                value={lotInput}
                onChange={(e) => setLotInput(e.target.value)}
                helperText="Format: R-9077, R-9763 (séparés par virgule ou ligne)"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                onClick={handleDiagnosticLoad}
                disabled={loading || !lotInput.trim()}
              >
                {loading ? 'Chargement...' : 'Charger Diagnostic'}
              </Button>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {diagnosticResults && (
            <Box sx={{ mt: 3 }}>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="h4" color="primary">
                        {diagnosticResults.lotsFound}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Lots trouvés
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="h4" color="info.main">
                        {diagnosticResults.totalSamples}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Échantillons totaux
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="h4" color="error.main">
                        {diagnosticResults.lotsNotFound}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Lots non trouvés
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {diagnosticResults.lotsNotFound > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Lots non trouvés: {diagnosticResults.notFoundLots.join(', ')}
                </Alert>
              )}

              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Lot</strong></TableCell>
                      <TableCell><strong>Échantillons</strong></TableCell>
                      <TableCell><strong>Statut</strong></TableCell>
                      <TableCell><strong>Certificat</strong></TableCell>
                      <TableCell><strong>AU Analyse</strong></TableCell>
                      <TableCell><strong>ICP Analyse</strong></TableCell>
                      <TableCell><strong>AU Valid.</strong></TableCell>
                      <TableCell><strong>ICP Valid.</strong></TableCell>
                      <TableCell><strong>BGM</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {diagnosticResults.lots.map((lot: any) => (
                      <TableRow key={lot.lotNumber}>
                        <TableCell><strong>{lot.lotNumber}</strong></TableCell>
                        <TableCell>{lot.sampleCount}</TableCell>
                        <TableCell>
                          <Chip label={lot.status} size="small" color="primary" />
                        </TableCell>
                        <TableCell>
                          {lot.certificateNumber ? (
                            <Chip 
                              label={`${lot.certificateNumber} (${lot.certificateStatus})`} 
                              size="small" 
                              color="success" 
                            />
                          ) : (
                            <Chip label="Non" size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          {lot.auAnalysisCompleted ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : (
                            '❌'
                          )}
                        </TableCell>
                        <TableCell>
                          {lot.icpAnalysisCompleted ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : (
                            '❌'
                          )}
                        </TableCell>
                        <TableCell>
                          {lot.auValidationCompleted ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : (
                            '❌'
                          )}
                        </TableCell>
                        <TableCell>
                          {lot.icpValidationCompleted ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : (
                            '❌'
                          )}
                        </TableCell>
                        <TableCell>
                          {lot.bgmMethodSelected ? (
                            <Chip label="BGM" size="small" color="warning" />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </TabPanel>

        {/* ==================== TAB 2: SEND TO VALIDATION ==================== */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            🔄 Renvoyer Lots à Validation
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Renvoyez des lots générés ou livrés vers les dashboards de validation (AU/ICP Superviseur). Les certificats seront supprimés et les flags de validation réinitialisés.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Numéros de Lots"
                placeholder="R-9077, R-9763, R-9430&#10;ou un lot par ligne"
                value={lotInput}
                onChange={(e) => setLotInput(e.target.value)}
                helperText="Format: R-9077, R-9763 (séparés par virgule ou ligne)"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                onClick={handleSendValidationPreview}
                disabled={loading || !lotInput.trim()}
              >
                {loading ? 'Chargement...' : 'Prévisualiser'}
              </Button>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}

          {sendValidationPreview && !sendValidationResult && (
            <Box sx={{ mt: 3 }}>
              <Card sx={{ mb: 2, bgcolor: 'info.lighter' }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography variant="h4" color="primary">
                        {sendValidationPreview.lotsFound}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Lots à traiter
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="h4" color="info.main">
                        {sendValidationPreview.totalSamples}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Échantillons
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="h4" color="warning.main">
                        {sendValidationPreview.certificatesToDelete}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Certificats à supprimer
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="h4" color="error.main">
                        {sendValidationPreview.lotsNotFound}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Lots non trouvés
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {sendValidationPreview.lotsNotFound > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Lots non trouvés: {sendValidationPreview.notFoundLots.join(', ')}
                </Alert>
              )}

              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Lot</strong></TableCell>
                      <TableCell><strong>Échantillons</strong></TableCell>
                      <TableCell><strong>Statut Actuel</strong></TableCell>
                      <TableCell><strong>Nouveau Statut</strong></TableCell>
                      <TableCell><strong>Certificat</strong></TableCell>
                      <TableCell><strong>AU Validé</strong></TableCell>
                      <TableCell><strong>ICP Validé</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sendValidationPreview.lots.map((lot: any) => (
                      <TableRow key={lot.lotNumber}>
                        <TableCell><strong>{lot.lotNumber}</strong></TableCell>
                        <TableCell>{lot.sampleCount}</TableCell>
                        <TableCell>
                          <Chip label={lot.currentStatus} size="small" color="default" />
                        </TableCell>
                        <TableCell>
                          <Chip label={lot.targetStatus} size="small" color="primary" />
                        </TableCell>
                        <TableCell>
                          {lot.hasCertificate ? (
                            <Chip 
                              label={`${lot.certificateNumber} (sera supprimé)`} 
                              size="small" 
                              color="warning" 
                            />
                          ) : (
                            <Chip label="Non" size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          {lot.auValidated ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : (
                            '❌'
                          )}
                        </TableCell>
                        <TableCell>
                          {lot.icpValidated ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : (
                            '❌'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendValidationConfirmed}
                      onChange={(e) => setSendValidationConfirmed(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Je confirme vouloir renvoyer ces lots à validation"
                />
              </Box>

              <Button
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} /> : <RestoreIcon />}
                onClick={handleSendValidationExecute}
                disabled={loading || !sendValidationConfirmed}
              >
                {loading ? 'Exécution...' : 'Exécuter'}
              </Button>
            </Box>
          )}

          {sendValidationResult && (
            <Box sx={{ mt: 3 }}>
              <Card sx={{ bgcolor: 'success.lighter' }}>
                <CardContent>
                  <Typography variant="h6" color="success.main" gutterBottom>
                    ✅ Opération réussie
                  </Typography>
                  <Typography variant="body2">
                    • Lots traités: <strong>{sendValidationResult.lotsProcessed}</strong>
                  </Typography>
                  <Typography variant="body2">
                    • Échantillons mis à jour: <strong>{sendValidationResult.samplesUpdated}</strong>
                  </Typography>
                  <Typography variant="body2">
                    • Certificats supprimés: <strong>{sendValidationResult.certificatesDeleted}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Les lots apparaîtront maintenant dans les dashboards AU/ICP Superviseur
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}
        </TabPanel>

        {/* ==================== TAB 3: DELETE LOTS ==================== */}
        <TabPanel value={tabValue} index={2}>
          <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
            <Typography variant="h6">⚠️ OPÉRATION DANGEREUSE</Typography>
            <Typography variant="body2">
              Cette action supprime DÉFINITIVEMENT les lots et tous leurs échantillons. Cette opération est IRRÉVERSIBLE.
              Assurez-vous de créer une sauvegarde de la base de données avant de procéder.
            </Typography>
          </Alert>

          <Typography variant="h6" gutterBottom color="error.main">
            🗑️ Supprimer Lots (PERMANENT)
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Supprime définitivement les lots, tous leurs échantillons et certificats associés.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Numéros de Lots"
                placeholder="AFL-0002, AFL-0003&#10;ou un lot par ligne"
                value={lotInput}
                onChange={(e) => setLotInput(e.target.value)}
                helperText="Format: AFL-0002, AFL-0003 (séparés par virgule ou ligne)"
                error={Boolean(lotInput.trim())}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                color="error"
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                onClick={handleDeletePreview}
                disabled={loading || !lotInput.trim()}
              >
                {loading ? 'Chargement...' : 'Prévisualiser Suppression'}
              </Button>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}

          {deletePreview && !deleteResult && (
            <Box sx={{ mt: 3 }}>
              <Card sx={{ mb: 2, bgcolor: 'error.lighter', border: '2px solid', borderColor: 'error.main' }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="h4" color="error.main">
                        {deletePreview.lotsFound}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Lots à SUPPRIMER
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="h4" color="error.main">
                        {deletePreview.totalSamples}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Échantillons à SUPPRIMER
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="h4" color="error.main">
                        {deletePreview.certificatesToDelete}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Certificats à SUPPRIMER
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {deletePreview.warnings && deletePreview.warnings.length > 0 && (
                <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
                  {deletePreview.warnings.map((warning: string, idx: number) => (
                    <Typography key={idx} variant="body2">{warning}</Typography>
                  ))}
                </Alert>
              )}

              {deletePreview.lotsNotFound > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Lots non trouvés: {deletePreview.notFoundLots.join(', ')}
                </Alert>
              )}

              <TableContainer component={Paper} sx={{ mb: 2, border: '1px solid', borderColor: 'error.main' }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: 'error.lighter' }}>
                    <TableRow>
                      <TableCell><strong>Lot</strong></TableCell>
                      <TableCell><strong>Échantillons</strong></TableCell>
                      <TableCell><strong>Statut</strong></TableCell>
                      <TableCell><strong>Certificat</strong></TableCell>
                      <TableCell><strong>Référence Client</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deletePreview.lots.map((lot: any) => (
                      <TableRow key={lot.lotNumber}>
                        <TableCell><strong>{lot.lotNumber}</strong></TableCell>
                        <TableCell>{lot.sampleCount}</TableCell>
                        <TableCell>
                          <Chip label={lot.status} size="small" color="default" />
                        </TableCell>
                        <TableCell>
                          {lot.hasCertificate ? (
                            <Chip 
                              label={lot.certificateNumber} 
                              size="small" 
                              color="error" 
                            />
                          ) : (
                            <Chip label="Non" size="small" />
                          )}
                        </TableCell>
                        <TableCell>{lot.clientReference}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  CONFIRMATION REQUISE
                </Typography>
                <Typography variant="body2">
                  Cette action est PERMANENTE et IRRÉVERSIBLE. Assurez-vous d'avoir créé une sauvegarde.
                </Typography>
              </Alert>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={deleteConfirmed}
                        onChange={(e) => setDeleteConfirmed(e.target.checked)}
                        color="error"
                      />
                    }
                    label="J'ai créé une sauvegarde et je confirme vouloir supprimer définitivement ces lots"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Tapez DELETE pour confirmer"
                    placeholder="DELETE"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    error={deleteConfirmText !== '' && deleteConfirmText !== 'DELETE'}
                    helperText="Vous devez taper exactement 'DELETE' (en majuscules)"
                  />
                </Grid>
              </Grid>

              <Button
                variant="contained"
                color="error"
                startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
                onClick={handleDeleteExecute}
                disabled={loading || !deleteConfirmed || deleteConfirmText !== 'DELETE'}
              >
                {loading ? 'Suppression...' : 'SUPPRIMER DÉFINITIVEMENT'}
              </Button>
            </Box>
          )}

          {deleteResult && (
            <Box sx={{ mt: 3 }}>
              <Card sx={{ bgcolor: 'warning.lighter', border: '2px solid', borderColor: 'error.main' }}>
                <CardContent>
                  <Typography variant="h6" color="error.main" gutterBottom>
                    ⚠️ Suppression terminée
                  </Typography>
                  <Typography variant="body2">
                    • Lots supprimés: <strong>{deleteResult.lotsDeleted}</strong>
                  </Typography>
                  <Typography variant="body2">
                    • Échantillons supprimés: <strong>{deleteResult.samplesDeleted}</strong>
                  </Typography>
                  <Typography variant="body2">
                    • Certificats supprimés: <strong>{deleteResult.certificatesDeleted}</strong>
                  </Typography>
                  <Typography variant="body2" color="error.main" fontWeight="bold" sx={{ mt: 1 }}>
                    {deleteResult.warning}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}
        </TabPanel>
      </Paper>
    </Box>
  );
}

